import React, {useState , useCallback} from 'react';
import { ReactFlow,
  Background,
  EdgeProps,
  Handle,
  Position,
  Node,
  Edge,
  useNodesState,
  useEdgesState,
  useReactFlow,
  getBezierPath,
  addEdge,
  DefaultEdgeOptions,
  ReactFlowProvider
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

const NODE_WIDTH = 150;
const NODE_HEIGHT = 40;
const BUFFER = 40;
const canvasWidth = document.body.clientWidth;

type CustomNodeData = {
  label: string;
  isConnected: boolean;
};

const CustomNode: React.FC<{ data: CustomNodeData,  }> = ({ data }) => (
  <>
    <Handle type="target" position={Position.Top} />
    <div
      style={{
        background: data.isConnected ? 'red': 'white',
        border: '1px solid black',
        borderRadius: '3px',
        padding: '10px',
        width: `${NODE_WIDTH}px`,
        height: `${NODE_HEIGHT}px`,
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        fontSize: '12px',
      }}
    >
      {data.label}
    </div>
    <Handle type="source" position={Position.Bottom} />
  </>
);

const isNodeInPath = (
  node: Node,
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number
): boolean => {
  const buffer = 20;
  const left = Math.min(sourceX, targetX) - buffer;
  const right = Math.max(sourceX, targetX) + buffer;
  const top = Math.min(sourceY, targetY) - buffer;
  const bottom = Math.max(sourceY, targetY) + buffer;

  return (
    node.position.x + NODE_WIDTH > left &&
    node.position.x < right &&
    node.position.y + NODE_HEIGHT > top &&
    node.position.y < bottom
  );
};

// *********************************** smooth step edge **********************************

// const calculateOrthogonalPath = (
//   sourceX: number,
//   sourceY: number,
//   targetX: number,
//   targetY: number,
//   nodes: Node[]
// ): string => {
//   const midX = (sourceX + targetX) / 2;
//   const buffer = 40;
//   const curveBuffer = 20;
  
//   // Calculate the Y positions 20px above or below the sourceY and targetY based on the relative position
//   const adjustedSourceY = sourceY < targetY ? sourceY + curveBuffer : sourceY - curveBuffer;
//   const adjustedTargetY = targetY < sourceY ? targetY + curveBuffer : targetY - curveBuffer;

//   // Find all nodes between the source and target Y positions
//   const relevantNodes = nodes.filter(node => 
//     node.position.y > Math.min(sourceY, targetY) && 
//     node.position.y < Math.max(sourceY, targetY)
//   );
  
//   if (relevantNodes.length > 0) {
//     // Find the leftmost and rightmost X positions of all relevant nodes
//     const leftmostX = Math.min(...relevantNodes.map(n => n.position.x)) - buffer;
//     const rightmostX = Math.max(...relevantNodes.map(n => n.position.x + NODE_WIDTH)) + buffer;
    
//     // Choose the path that goes around all nodes
//     if (Math.abs(sourceX - leftmostX) < Math.abs(sourceX - rightmostX)) {
//       return `M${sourceX},${sourceY} V${adjustedSourceY} H${leftmostX} V${adjustedTargetY} H${targetX} V${targetY}`;
//     } else {
//       return `M${sourceX},${sourceY} V${adjustedSourceY} H${rightmostX} V${adjustedTargetY} H${targetX} V${targetY}`;
//     }
//   }
  
//   // If no interfering nodes, use a simple path
//   return `M${sourceX},${sourceY} V${adjustedSourceY} H${midX} V${adjustedTargetY} H${targetX} V${targetY}`;
// };

// ********************* curved step edge *********************************************8
const calculateOrthogonalPath = (
  sourceX: number,
  sourceY: number,
  targetX: number,
  targetY: number,
  nodes: Node[]
): string => {
  const buffer = 40;
  const curveBuffer = 20;

  // Find all nodes between the source and target Y positions
  const relevantNodes = nodes.filter(node =>
    node.position.y > Math.min(sourceY, targetY) &&
    node.position.y < Math.max(sourceY, targetY)
  );

  if (relevantNodes.length > 0) {
    // Find the leftmost and rightmost X positions of all relevant nodes
    const leftmostX = Math.min(...relevantNodes.map(n => n.position.x)) - buffer;
    const rightmostX = Math.max(...relevantNodes.map(n => n.position.x + NODE_WIDTH)) + buffer;

    // Choose the path that goes around all nodes
    const controlX = Math.abs(sourceX - leftmostX) < Math.abs(sourceX - rightmostX) ? leftmostX : rightmostX;

    // Calculate control points for the Bezier curve
    const sourceControlY = sourceY < targetY ? sourceY + curveBuffer : sourceY - curveBuffer;
    const targetControlY = targetY < sourceY ? targetY + curveBuffer : targetY - curveBuffer;

    return `M${sourceX},${sourceY} 
            C${sourceX},${sourceControlY} ${controlX},${sourceControlY} ${controlX},${(sourceY + targetY) / 2}
            S${controlX},${targetControlY} ${targetX},${targetY}`;
  }

  // If no interfering nodes, use a simple curved path
  const midX = (sourceX + targetX) / 2;
  const sourceControlY = sourceY < targetY ? sourceY + curveBuffer : sourceY - curveBuffer;
  const targetControlY = targetY < sourceY ? targetY + curveBuffer : targetY - curveBuffer;

  return `M${sourceX},${sourceY} 
          C${sourceX},${sourceControlY} ${midX},${sourceControlY} ${midX},${(sourceY + targetY) / 2}
          S${midX},${targetControlY} ${targetX},${targetY}`;
};


const CustomEdge: React.FC<EdgeProps> = ({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style = {},
  data,
}:any) => {
  const edgePath = calculateOrthogonalPath(sourceX, sourceY, targetX, targetY, data?.nodes || []);

  return (
    <path
      id={id}
      style={style}
      className="react-flow__edge-path"
      d={edgePath}
      fill="none"
    />
  );
};

const nodeTypes = {
  custom: CustomNode,
};

const edgeTypes = {
  custom: CustomEdge,
};

function calculateNodePositions(
  nodes: any[],
  levelWidth: number = 300,
  nodeSpacing: number = 200
): { [id: string]: { x: number; y: number } } {
  const positions: { [id: string]: { x: number; y: number } } = {};
  const levelCounts: { [level: number]: number } = {};

  nodes.forEach((node) => {
    if (levelCounts[node.level] === undefined) {
      levelCounts[node.level] = 0;
    }
    node.column = levelCounts[node.level];
    levelCounts[node.level]++;
  });

  nodes.forEach((node) => {
    const x = (node.column! - (levelCounts[node.level] - 1) / 2) * nodeSpacing;
    const y = node.level * levelWidth;
    positions[node.id] = { x, y };
  });

  return positions;
}

const initialNodes:any[] = [
  { id: 'node1', data: { label: 'Node1' }, type: 'custom', position: { x: 0, y: 0 }, level: 0 },
  { id: 'node2', data: { label: 'Node 2' }, type: 'custom', position: { x: 0, y: 0 }, level: 1 },
  { id: 'node3', data: { label: 'Node 3' }, type: 'custom', position: { x: 0, y: 0 }, level: 1 },
  { id: 'node4', data: { label: 'Node 4' }, type: 'custom', position: { x: 0, y: 0 }, level: 2 },
  { id: 'node5', data: { label: 'Node 5' }, type: 'custom', position: { x: 0, y: 0 }, level: 2 },
  { id: 'node6', data: { label: 'Node 6' }, type: 'custom', position: { x: 0, y: 0 }, level: 2 },
  { id: 'node7', data: { label: 'Node 7' }, type: 'custom', position: { x: 0, y: 0 }, level: 0 },
  { id: 'node8', data: { label: 'Node 8' }, type: 'custom', position: { x: 0, y: 0 }, level: 1 },
  { id: 'node9', data: { label: 'Node 9' }, type: 'custom', position: { x: 0, y: 0 }, level: 1 },
  { id: 'node10', data: { label: 'Node 10' }, type: 'custom', position: { x: 0, y: 0 }, level: 3 },
  { id: 'node11', data: { label: 'Node 11' }, type: 'custom', position: { x: 0, y: 0 }, level: 3 },
  { id: 'node12', data: { label: 'Node 12' }, type: 'custom', position: { x: 0, y: 0 }, level: 3 },
];

const initialEdges: Edge[] = [
  { id: 'e1-2', source: 'node1', target: 'node2', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e1-3', source: 'node1', target: 'node3', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e2-4', source: 'node2', target: 'node4', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e2-5', source: 'node2', target: 'node5', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e3-6', source: 'node3', target: 'node6', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e7-8', source: 'node7', target: 'node8', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e7-9', source: 'node7', target: 'node9', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e1-6', source: 'node1', target: 'node6', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e7-10', source: 'node7', target: 'node10', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e7-6', source: 'node7', target: 'node6', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e8-10', source: 'node8', target: 'node10', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e2-10', source: 'node2', target: 'node10', type: 'custom', data: { nodes: initialNodes } },
  { id: 'e3-10', source: 'node3', target: 'node10', type: 'custom', data: { nodes: initialNodes } },
  // { id: 'e7-12', source: 'node7', target: 'node12', type: 'custom', data: { nodes: initialNodes } },
];

const positions = calculateNodePositions(initialNodes);
initialNodes.forEach((node) => {
  const pos = positions[node.id];
  node.position = { x: pos.x, y: pos.y };
});

const HierarchicalFlow: React.FC = () => {
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const [connectedNodeIds, setConnectedNodeIds] = useState<string[]>([]);

  const { getNode } = useReactFlow();

  const onEdgeClick = useCallback((event: React.MouseEvent, edge: Edge) => {
    setConnectedNodeIds([edge.source, edge.target]);
  }, []);

  const onPaneClick = useCallback(() => {
    setConnectedNodeIds([]);
  }, []);

  const onConnect = useCallback(
    (params:any) =>
      setEdges((eds) =>
        addEdge(
          { ...params },
          eds,
        ),
      ),
    [],
  );

  const nodesWithHighlight = nodes.map(node => ({
    ...node,
    data: {
      ...node.data,
      isConnected: connectedNodeIds.includes(node.id),
    },
    
  }));

  const defaultEdgeOptions: DefaultEdgeOptions = {
    type: 'custom'
  }

  return (
    <ReactFlow
    nodes={nodesWithHighlight}
      edges={edges}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      nodeTypes={nodeTypes}
      edgeTypes={edgeTypes}
      onEdgeClick={onEdgeClick}
      onPaneClick={onPaneClick}
      onConnect={onConnect}
      defaultEdgeOptions={defaultEdgeOptions}
      fitView
    >
      
      <Background />
    </ReactFlow>
  );
};

const FlowLevelModel = () => {
    return (
        <ReactFlowProvider>
            <HierarchicalFlow />
        </ReactFlowProvider>
    )
};

export default FlowLevelModel;
